import { inject, Injectable } from '@angular/core';
import { HappyTenantContextService } from '../happy/happy-tenant-context.service';
import { CacheVersionsMap } from '../../interfaces/cache-versions/cache-versions.interface';

const KEY_PREFIX = 'tenant-cache-versions:';

/**
 * Persists the latest known cache versions map per tenant in `localStorage`.
 *
 * Kept intentionally separate from the NGXS storage plugin so that an NGXS
 * state reset (e.g. clearing cart/orders) does not also wipe the version
 * tracking - which would otherwise force a full invalidation on the next
 * reconcile.
 */
@Injectable({ providedIn: 'root' })
export class CacheVersionsStorageService {
  private readonly tenantContext: HappyTenantContextService = inject(
    HappyTenantContextService
  );

  load(): CacheVersionsMap | null {
    const key = this.buildKey();
    if (!key || typeof window === 'undefined') {
      return null;
    }

    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object'
        ? (parsed as CacheVersionsMap)
        : null;
    } catch {
      return null;
    }
  }

  save(map: CacheVersionsMap): void {
    const key = this.buildKey();
    if (!key || typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(key, JSON.stringify(map));
    } catch {
      // Silently ignore quota/availability failures.
    }
  }

  clear(): void {
    const key = this.buildKey();
    if (!key || typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.removeItem(key);
    } catch {
      // Silently ignore failures.
    }
  }

  private buildKey(): string | null {
    const tenant = this.tenantContext.tenant();
    if (!tenant) {
      return null;
    }
    return `${KEY_PREFIX}${tenant}`;
  }
}
